import { CDC_START_SCREEN_MAP, IS_STORAGE_AVAILABLE, LOCAL_STORAGE_MAP } from '@/setup/globals';
import i18n from '@/setup/i18n.js';
import useNotificationStore from '@/stores/notification';
import useSessionStore from '@/stores/session';
import api from '@/helpers/api.js';
import { axios } from '@/plugins/axios.js';
import { isLoginEnabled, redirectUrl, reloadBrowserWindow } from '@/plugins/cdc/auth/auth-shared.js';
import { performLogout } from '@/plugins/cdc/auth/cdc-logout.js';
import { getUserByPayload, showCDCScreenSet, showErrorNotification } from '@/plugins/cdc/shared.js';


/**
 * @type {String} A reference to the screen-set to be presented.
 * The value of this parameter is the ID of a screen-set.
 */
export const SCREEN_SET_NAME = 'General-RegistrationLogin';

/**
 * Opens the login window.
 *
 * @param {Boolean} register - If true start screen set with register screen.
 * @param {String} [redirectTo] - Accepts an optional redirect url to be called after login.
 */
export function openLogin(register = false, redirectTo = null) {
  if (!isLoginEnabled) {
    return;
  }

  redirectUrl.value = redirectTo;

  if (window.gigya?.accounts) {
    showCDCScreenSet({
      screenSet: SCREEN_SET_NAME,
      startScreen: register ? CDC_START_SCREEN_MAP.register : null,
    });
  } else {
    showErrorNotification(i18n.global.t('globalMessages.loginUnavailable'));
  }
}

/**
 * Event handler for a successful login.
 * After a successful login it has to be delivered to the pimcore backend to log in the user in pimcore.
 * This callback is also triggered when the user has a valid SSO and is already logged in.
 *
 * Keep documentation up to date: https://collaboration.ivoclarvivadent.com/display/CGCM/Login+Redirect
 *
 * @param {Object} event - The event delivered by gigya.
 */
export function onLogin(event) {
  const sessionStore = useSessionStore();
  const payload = getUserByPayload(event);

  if (!payload.UID) {
    return;
  }

  /**
   * Prepares the pimcore login execution.
   */
  const internalPimcoreLogin = () => {
    performPimcoreLogin(payload);
  };

  sessionStore.globalLoader = true;

  // The outer pimcoreLoginPreCheck is a temporary solution to check if the user was activated. See #CA-3188.
  api
    .post(api.getUrl('authentication-pre-check'), payload)
    .then(() => {
      // If the user is validated on SalesForce and eCommerce.
      performCommerceLogin(event)
        .then(internalPimcoreLogin)
        .catch((error) => {
          // Errors are logged within the commerce login logic.
          sessionStore.globalLoader = false;

          throw error;
        });
    })
    .catch((error) => {
      sessionStore.globalLoader = false;

      switch (error?.response?.status) {
        case 412: // User is active but not validated.
          internalPimcoreLogin();

          break;

        // case 403: User is inactive.
        // Falls through

        default:
          // eslint-disable-next-line no-console
          console.error('CDC pre-check failed. Trigger logout');
          performLogout();
      }
    });
}

/**
 * Signs the user in on the e-commerce side.
 *
 * @param {Object} gigyaData - The gigya login response object.
 *
 * @returns {Promise}
 */
export function performCommerceLogin(gigyaData) {
  const notificationStore = useNotificationStore();
  const sessionStore = useSessionStore();
  const urlFormData = new URLSearchParams();
  const url = sessionStore.isMiniPortal ? api.getUrl('portal-login') : api.getUrl('shop-login');

  urlFormData.append('gigyaData', JSON.stringify(gigyaData));

  // Axios was used to prevent default error handling.
  return axios
    .post(url, urlFormData) // Type FormData is required for SAP
    .catch(() => ({
      // Handle invalid requests as valid.
      data: {
        code: 500,
      },
    }))
    .then((response) => {
      const code = response.data?.code;
      const notificationStub = {
        expire: false,
        message: {
          type: 'error',
          message: i18n.global.t('globalMessages.unknownApiError'),
        },
      };

      // eslint-disable-next-line no-console
      console.info(`CDC performCommerceLogin: Code: ${code} Response: ${response}`);

      switch (code) {
        case 200:
          return response;

        case 403:
          notificationStub.title = i18n.global.t('globalMessages.userLockedTitle');
          notificationStub.message.message = i18n.global.t('globalMessages.userLocked');

          notificationStore.showNotification(notificationStub);

          throw new Error('User is locked.');

        // 401 shouldn't occur anymore.
        case 401:
          // eslint-disable-next-line no-console
          console.warn({
            type: 'warning',
            title: i18n.global.t('globalMessages.userAccountNotPreparedTitle'),
            message: i18n.global.t('globalMessages.userAccountNotPrepared'),
          });

          return response;

        case 307:
          notificationStub.title = i18n.global.t('globalMessages.userAssignedToDifferentSiteTitle');

          notificationStub.message = {
            type: 'warning',
            message: i18n.global.t('globalMessages.userAssignedToDifferentSite'),
          };

          notificationStore.showNotification(notificationStub);

          return response;

        default:
          // Normally there should be a Promise reject here, but we don't know exactly which status codes commerce is sending.
          return response;
      }
    });
}

/**
 * Sends the logged in cdc user to the pimcore backend to login (or register) the user in pimcore.
 *
 * @param {Object} userData - The user object returned from cdc.
 */
export function performPimcoreLogin(userData) {
  const sessionStore = useSessionStore();
  const payload = {
    ...userData,
    referer: window.location.href,
  };

  if (!payload.UID) {
    return;
  }

  api
    .post(api.getUrl('authentication-login'), payload)
    .then((response) => {
      const { url } = response.data?.data || {};

      if (IS_STORAGE_AVAILABLE) {
        localStorage.setItem(LOCAL_STORAGE_MAP.cdcShowProgressiveProfiling, 'true');
      }

      sessionStore.globalLoader = false;

      if (redirectUrl.value) {
        const newUrl = redirectUrl.value;

        redirectUrl.value = null;

        reloadBrowserWindow(newUrl);
      } else if (url) {
        reloadBrowserWindow(url);
      } else {
        reloadBrowserWindow(null);
      }
    })
    .catch((error) => {
      sessionStore.globalLoader = false;

      throw error;
    });
}
